import { useEffect, useState } from 'react';
import type { TimedGameSnapshot } from '@wx-weiqi/contracts';
import type { GameStore } from '../stores/game-store';
import './current-games.css';

export function roomInvitationEligible(snapshot: TimedGameSnapshot, membershipId: string) {
  return (
    ['CREATED', 'WAITING'].includes(snapshot.status) &&
    snapshot.room.creatorMembershipId === membershipId &&
    !snapshot.room.players.some((player) => player.membershipId !== membershipId && player.ready)
  );
}

export function RoomInvitation({
  game,
  snapshot,
  membershipId,
  onCopy,
  copied,
  copyError,
}: {
  readonly game: Pick<GameStore, 'loadPlayersPresence'>;
  readonly snapshot: TimedGameSnapshot;
  readonly membershipId: string;
  readonly onCopy: () => void;
  readonly copied: boolean;
  readonly copyError: boolean;
}) {
  const [enteredGameId, setEnteredGameId] = useState<string | null>(null);
  const [presenceError, setPresenceError] = useState(false);
  const eligible = roomInvitationEligible(snapshot, membershipId);
  const visible = eligible && enteredGameId !== snapshot.gameId;
  const opponentId = snapshot.room.players.find(
    (player) => player.membershipId !== membershipId,
  )?.membershipId;
  useEffect(() => {
    if (!visible || opponentId === undefined) return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const online = await game.loadPlayersPresence(snapshot.gameId);
        if (disposed) return;
        setPresenceError(false);
        if (online.includes(opponentId)) {
          setEnteredGameId(snapshot.gameId);
          return;
        }
      } catch {
        if (disposed) return;
        setPresenceError(true);
      }
      if (!disposed) timer = setTimeout(() => void poll(), 2000);
    };
    void poll();
    return () => {
      disposed = true;
      clearTimeout(timer);
    };
  }, [game, snapshot.gameId, opponentId, visible]);
  if (!visible) return null;
  return (
    <div className="room-invitation-overlay">
      <section
        className="room-invitation"
        role="dialog"
        aria-labelledby="room-invitation-title"
        aria-describedby="room-invitation-description"
      >
        <h2 id="room-invitation-title">等待对手进入房间</h2>
        <p id="room-invitation-description">将房间码分享给对手，对方进入后此提示会自动关闭。</p>
        <span className="room-invitation__label">房间码</span>
        <strong className="room-invitation__code">{snapshot.room.roomCode}</strong>
        <button className="primary-button" type="button" onClick={onCopy}>
          {copied ? '已复制房间码' : '复制房间码'}
        </button>
        <p role="status">
          {copyError
            ? '复制未成功，请长按或选中上方房间码手动复制。'
            : presenceError
              ? '暂时无法确认对手是否进入，正在重试…'
              : '返回大厅后，可从“我的对局列表”再次进入。'}
        </p>
      </section>
    </div>
  );
}
